import { Field, Input, Spin, TextArea } from '@/components/commons';
import { Button, Form, Image, Upload } from 'antd';
import { useFormContext } from 'react-hook-form';
import { CloseOutlined } from '@ant-design/icons';
import {
  FALLBACK_URL,
  FILE_SIZE_LIMIT,
  SUPPORTED_EXTENSION,
  SUPPORTED_FORMATS,
} from '@/services/image';
import { FormField } from '@/components/commons/FormField';
import { PropsWithChildren } from 'react';

const UploadImageWrapper = (props: PropsWithChildren) => {
  return (
    <div className='border-solid rounded border-2 border-zinc-300  w-[200px] h-[100px] flex justify-center items-center'>
      {props.children}
    </div>
  );
};

interface IProps {
  isLoading: boolean;
  openDrawer: boolean;
}

export const PartnerForm: React.FC<IProps> = ({ isLoading, openDrawer }) => {
  const {
    watch,
    setValue,
    setError,
    formState: { errors },
    clearErrors,
  } = useFormContext();

  const coverImage = watch('coverImage');
  const profileImage = watch('profileImage');
  const attachments = watch('attachments');
  const profileAttachments = watch('profileAttachments');

  const handleRemoveCoverImage = () => {
    setValue('coverImage', null);
    setValue('attachments', {});
  };

  const handleRemoveProfileImage = () => {
    setValue('profileImage', null);
    setValue('profileAttachments', {});
  };

  const beforeUploadCover = (file: File): boolean | string => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (file.size > FILE_SIZE_LIMIT) {
      setError('attachment', {
        type: 'validate',
        message: '画像のサイズは10MBを超えています。',
      });
      // setValue('attachments', {});
      return Upload.LIST_IGNORE;
    }

    if (
      !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
      !SUPPORTED_FORMATS.includes(file.type)
    ) {
      setError('attachment', {
        type: 'validate',
        message: '画像は .jpg、.png、.jpeg 形式ではありません。',
      });
      // setValue('attachments', null);
      return false;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setValue('coverImage', reader.result as string);
      setValue('attachments', { file, fileList: [{ originFileObj: file }] });
    };
    reader.readAsDataURL(file);
    clearErrors(['attachment', 'coverImage']);

    return Upload.LIST_IGNORE;
  };

  const beforeUploadProfile = (file: File): boolean | string => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (file.size > FILE_SIZE_LIMIT) {
      setError('profileAttachment', {
        type: 'validate',
        message: '画像のサイズは10MBを超えています。',
      });
      return Upload.LIST_IGNORE;
    }

    if (
      !SUPPORTED_EXTENSION.includes(fileExtension || '') ||
      !SUPPORTED_FORMATS.includes(file.type)
    ) {
      setError('profileAttachment', {
        type: 'validate',
        message: '画像は .jpg、.png、.jpeg 形式ではありません。',
      });
      return Upload.LIST_IGNORE;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setValue('profileImage', reader.result as string);
      setValue('profileAttachments', {
        file,
        fileList: [{ originFileObj: file }],
      });
    };
    reader.readAsDataURL(file);
    clearErrors(['profileAttachment', 'profileImage']);

    return Upload.LIST_IGNORE;
  };

  return (
    <Form layout='vertical'>
      <Spin spinning={isLoading}>
        <Field label='企業名' name='companyName' required>
          <Input placeholder='企業名' maxLength={100} />
        </Field>

        <Field label='説明文章' name='description' required>
          <TextArea placeholder='説明文章' rows={4} maxLength={1000} />
        </Field>

        {!attachments?.fileList?.length && !coverImage && (
          <FormField
            label='カバー画像'
            name='attachments'
            required
            className='mb-0'
          >
            <div className='border-solid rounded border-2 border-zinc-300  w-[200px] h-[100px] flex justify-center items-center'>
              <Upload
                customRequest={() => {}}
                listType='text'
                maxCount={1}
                beforeUpload={beforeUploadCover}
                showUploadList={false}
              >
                <Button
                  type='default'
                  shape='default'
                  size='middle'
                  className='text-[#84ab42] border border-[#84ab42] font-semibold'
                >
                  画像を選択
                </Button>
              </Upload>
            </div>
          </FormField>
        )}

        {coverImage && openDrawer && !isLoading && (
          <Field label='カバー画像' name='coverImage' className='mb-0' required>
            <div className='relative inline-block'>
              <Image
                height={100}
                src={coverImage}
                className='block rounded-lg'
                fallback={FALLBACK_URL}
              />
              <Button
                type='primary'
                shape='circle'
                icon={<CloseOutlined />}
                size='small'
                onClick={handleRemoveCoverImage}
                className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
              />
            </div>
          </Field>
        )}
        <div className='my-2 font-medium text-xs'>
          <span className='font-bold mr-1'>※</span>
          1.8:1(~375×211 px)の写真（正方形）をアップロードしてください
        </div>
        <Field name='attachment' className='upload-field-error mb-0'>
          <div></div>
        </Field>
        {!profileAttachments?.fileList?.length && !profileImage && (
          <FormField
            label='プロフィール画像'
            name='profileAttachments'
            required
            className='mb-0'
          >
            <div className='border-solid rounded border-2 border-zinc-300  w-[120px] h-[120px] flex justify-center items-center'>
              <Upload
                customRequest={() => {}}
                listType='text'
                maxCount={1}
                beforeUpload={beforeUploadProfile}
                showUploadList={false}
              >
                <Button
                  type='default'
                  shape='default'
                  size='middle'
                  className='text-[#84ab42] border border-[#84ab42] font-semibold'
                >
                  画像を選択
                </Button>
              </Upload>
            </div>
          </FormField>
        )}

        {profileImage && openDrawer && !isLoading && (
          <Field
            label='プロフィール画像'
            name='profileImage'
            required
            className='mb-0'
          >
            <div className='relative inline-block'>
              <Image
                height={120}
                width={120}
                src={profileImage}
                className='block rounded-lg object-cover object-center'
                fallback={FALLBACK_URL}
              />
              <Button
                type='primary'
                shape='circle'
                icon={<CloseOutlined />}
                size='small'
                onClick={handleRemoveProfileImage}
                className='absolute -top-2 -right-2 z-10 bg-red-500 border-none'
              />
            </div>
          </Field>
        )}
        <div className='my-2 font-medium text-xs'>
          <span className='font-bold mr-1'>※</span>
          1:1の写真（正方形）をアップロードしてください
        </div>
        <Field name='profileAttachment' className='upload-field-error mb-0'>
          <div></div>
        </Field>

        <div className='mb-4'>
          <label className='block mb-2 font-semibold'>遷移先</label>
          <Field label='' name='url1' className='mb-2'>
            <Input
              placeholder='URL 1を入力'
              maxLength={2000}
              autoComplete='off'
            />
          </Field>
          <Field label='' name='url2' className='mb-2'>
            <Input
              placeholder='URL 2を入力'
              maxLength={2000}
              autoComplete='off'
            />
          </Field>
          <Field label='' name='url3' className='mb-2'>
            <Input
              placeholder='URL 3を入力'
              maxLength={2000}
              autoComplete='off'
            />
          </Field>
        </div>
      </Spin>
    </Form>
  );
};
